/**
 * Simple HTTP Client для интеграции с Backend API
 * Event Master v1.5 - Backend Integration
 */

import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { SecureStorage } from "../utils/secure-storage";
import { config } from "../config/env";

// Конфигурация для backend — SSOT через config.API_BASE_URL (REACT_APP_API_BASE_URL).
// По умолчанию на dev backend слушает :3001/api (фолбэк внутри config).
const BASE_URL = config.API_BASE_URL;

class HttpClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor для добавления токена (async для Secure Storage)
    this.client.interceptors.request.use(
      async (config) => {
        const token = await this.getAuthToken();
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor для обработки ошибок
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          await this.handleAuthError();
        }
        return Promise.reject(error);
      },
    );
  }

  private async getAuthToken(): Promise<string | null> {
    return SecureStorage.get("accessToken");
  }

  private async handleAuthError(): Promise<void> {
    await SecureStorage.remove("accessToken");
    await SecureStorage.remove("refreshToken");
    window.dispatchEvent(new CustomEvent("auth:logout"));
  }

  async get<T>(url: string, config?: AxiosRequestConfig) {
    return this.client.get<T>(url, config);
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.post<T>(url, data, config);
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.put<T>(url, data, config);
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.patch<T>(url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig) {
    return this.client.delete<T>(url, config);
  }
}

export const httpClient = new HttpClient();
